"use client";

import React from "react";
import { 
  Calendar, 
  User, 
  Clock, 
  Share2, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Tag, 
  ChevronRight,
  ArrowRight
} from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";



function PostMeta({ date, author, category }: { date?: string, author?: string, category?: string }) {
  return (
    <div data-section-id="29301" className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6 font-sans">
      {category && (
        <span className="bg-secondary/50 text-primary px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
          {category}
        </span>
      )}
      {author && (
        <div className="flex items-center gap-1.5">
          <User className="h-4 w-4 text-primary/70" />
          <span>{author}</span>
        </div>
      )}
      {date && (
        <div className="flex items-center gap-1.5">
          <Calendar className="h-4 w-4 text-primary/70" />
          <span>{new Date(date).toLocaleDateString("en-AU", { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      )}
    </div>
  );
}

function SocialShare() {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium mr-2 hidden sm:inline-block text-muted-foreground">Share Article:</span>
      <Button variant="outline" size="icon" className="h-9 w-9 rounded-full border-border hover:text-[#1877F2] hover:border-[#1877F2] transition-colors">
        <Facebook className="h-4 w-4" />
      </Button>
      <Button variant="outline" size="icon" className="h-9 w-9 rounded-full border-border hover:text-[#1DA1F2] hover:border-[#1DA1F2] transition-colors">
        <Twitter className="h-4 w-4" />
      </Button>
      <Button variant="outline" size="icon" className="h-9 w-9 rounded-full border-border hover:text-[#0A66C2] hover:border-[#0A66C2] transition-colors">
        <Linkedin className="h-4 w-4" />
      </Button>
      <Button variant="outline" size="icon" className="h-9 w-9 rounded-full border-border hover:text-primary hover:border-primary transition-colors">
        <Share2 className="h-4 w-4" />
      </Button>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse max-w-4xl mx-auto">
        <div className="h-10 bg-muted rounded-lg w-3/4" />
        <div className="h-4 bg-muted rounded w-1/2" />
        <div className="aspect-video bg-muted rounded-2xl w-full" />
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20 bg-card rounded-2xl border border-border">
        <p className="text-destructive mb-4 font-sans">We encountered an issue loading this resource.</p>
        <Button onClick={() => refetch()} className="rounded-full bg-primary text-primary-foreground">Try Again</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-20 bg-card rounded-2xl border border-border">
        <p className="text-muted-foreground font-sans">The requested article could not be found.</p>
        <Link to="/HomePage">
          <Button variant="link" className="mt-4 text-primary font-bold">Return to Homepage</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const category = post.categories?.[0]?.name || "Resources";

  return (
    <article className="max-w-4xl mx-auto bg-card shadow-md border border-border/40 rounded-2xl overflow-hidden">
      <div className="p-6 md:p-12">
        {}
        <header className="mb-10">
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-foreground leading-tight mb-6 tracking-tight">
            {post.title}
          </h1>
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-b border-border/60 pb-8">
            <PostMeta 
              date={post.date} 
              author={post.author?.name} 
              category={category} 
            />
            <SocialShare />
          </div>
        </header>

        {}
        {post.featuredImage && (
          <div className="mb-12 rounded-2xl overflow-hidden shadow-lg">
            <img 
              src={post.featuredImage} 
              alt={post.title} 
              className="w-full h-auto object-cover aspect-video hover:scale-105 transition-transform duration-1000 ease-in-out"
            />
          </div>
        )}

        {}
        <div 
          className="prose prose-xl max-w-none dark:prose-invert prose-headings:font-serif prose-headings:text-primary prose-headings:font-bold prose-a:text-primary prose-a:font-bold prose-img:rounded-2xl prose-img:shadow-lg text-foreground/80 font-sans leading-loose [&>p]:mb-8 [&>h2]:text-3xl [&>h2]:mt-12 [&>h2]:mb-6 [&>ul]:list-disc [&>ul]:pl-8 [&>ul]:mb-8 [&>blockquote]:border-l-4 [&>blockquote]:border-primary/30 [&>blockquote]:bg-secondary/20 [&>blockquote]:p-6 [&>blockquote]:rounded-r-lg"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {}
        <div className="mt-16 pt-10 border-t border-border/60">
          <div className="flex flex-wrap items-center gap-3 mb-10">
            <Tag className="h-4 w-4 text-primary/60" />
            <span className="text-sm font-bold text-muted-foreground mr-2 tracking-wide uppercase">Topics:</span>
            {post.tags && post.tags.length > 0 ? (
              post.tags.map((tag) => (
                <Link key={tag.id} to={tag.link || "#"}>
                  <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-semibold bg-muted hover:bg-secondary/40 hover:text-primary transition-all text-muted-foreground border border-transparent hover:border-secondary">
                    #{tag.name}
                  </span>
                </Link>
              ))
            ) : (
              <span className="text-sm text-muted-foreground italic">No related tags</span>
            )}
          </div>

          {}
          {post.author && (
            <div className="bg-secondary/10 rounded-2xl p-8 flex flex-col sm:flex-row gap-8 items-center sm:items-start text-center sm:text-left border border-secondary/20 shadow-sm">
              <Avatar className="h-24 w-24 border-4 border-background shadow-md">
                <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
                <AvatarFallback className="text-2xl font-serif font-bold bg-primary text-primary-foreground">
                  {post.author.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="text-xl font-serif font-bold text-primary mb-2">
                  About {post.author.name}
                </h3>
                <p className="text-base text-muted-foreground mb-6 leading-relaxed font-sans">
                  Dedicated advocate and educator specialized in Pathological Demand Avoidance. Committed to providing neuro-affirming support and evidence-based resources for Australian families and teachers.
                </p>
                <Link to={post.author.link || "#"}>
                  <Button variant="outline" size="sm" className="rounded-full px-6 font-semibold border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground transition-all">
                    View Contributor Profile
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-72 bg-muted rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <div key={post.id} data-index={i} className="group flex flex-col h-full bg-card rounded-2xl border border-border/40 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
          <Link to={post.link || "#"} className="block overflow-hidden relative aspect-video">
            {post.featuredImage ? (
              <img 
                src={post.featuredImage} 
                alt={post.title} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            ) : (
              <div className="w-full h-full bg-secondary/20 flex items-center justify-center text-primary/40">
                <span className="text-xs font-bold uppercase tracking-widest">PDA Australia</span>
              </div>
            )}
            <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors duration-300" />
          </Link>
          <div className="p-6 flex-1 flex flex-col">
            <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground mb-3 tracking-wide uppercase">
              <Clock className="h-3.5 w-3.5 text-primary/60" />
              <span>{post.date ? new Date(post.date).toLocaleDateString("en-AU") : "Recently Published"}</span>
            </div>
            <Link to={post.link || "#"} className="block group-hover:text-primary transition-colors">
              <h3 className="font-serif font-bold text-lg leading-snug mb-3 line-clamp-2">
                {post.title}
              </h3>
            </Link>
            <p className="text-sm text-muted-foreground line-clamp-3 mb-6 flex-1 font-sans leading-relaxed">
              {post.excerpt ? post.excerpt.replace(/<[^>]*>?/gm, '') : "Access expert insights and practical strategies for supporting neurodivergent individuals in Australia..."}
            </p>
            <Link to={post.link || "#"} className="mt-auto inline-flex items-center text-sm font-bold text-primary hover:translate-x-1 transition-transform">
              Read More <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}



export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = (typeof window !== 'undefined' && (window as any).wvcClient) 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : {};
    
  const mainQuery = new WP_Query(currentWPQueryParams);

  
  
  const relatedQuery = new WP_Query({ 
    per_page: 3,
    orderby: "date",
    order: "desc"
  });

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen py-16 md:py-24">
      <div className="container mx-auto px-6">
        
        {}
        <div className="max-w-4xl mx-auto mb-8 flex items-center gap-2 text-sm font-sans font-medium text-muted-foreground">
          <Link to="/HomePage" className="hover:text-primary transition-colors">Home</Link>
          <ChevronRight className="h-4 w-4 text-primary/40" />
          <Link to="/ArticlesPage" className="hover:text-primary transition-colors">Articles</Link>
          <ChevronRight className="h-4 w-4 text-primary/40" />
          <span className="text-foreground font-bold">Current Article</span>
        </div>

        {}
        <PostsProvider wp_query={mainQuery} className="mb-20">
          <SinglePostConsumer />
        </PostsProvider>

        {}
        <div className="max-w-6xl mx-auto mt-24 pt-16 border-t border-border/60">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-12">
            <h2 className="text-3xl font-serif font-bold text-primary relative inline-block">
              Further Reading
              <span className="absolute -bottom-3 left-0 w-16 h-1 bg-chart-2 rounded-full"></span>
            </h2>
            <Link to="/ArticlesPage">
              <Button variant="ghost" className="text-muted-foreground hover:text-primary font-bold rounded-full px-6">
                Explore All Articles <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={{ per_page: 3, orderby: "date", order: "desc" }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>

      </div>
    </section>
  );
}